import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { customOutletReviewService } from '@/services/customOutletReviewService';
import { ReviewActionDialog } from './ReviewActionDialog';
import type { CustomOutletReview } from '@/types/mediaOutlets';

export function CustomOutletReviewQueue() {
  const queryClient = useQueryClient();
  const [selectedReview, setSelectedReview] = useState<CustomOutletReview | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'merge' | null>(null);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['custom-outlet-reviews'],
    queryFn: () => customOutletReviewService.getPendingReviews(),
  });

  const approveMutation = useMutation({
    mutationFn: (reviewId: string) => customOutletReviewService.approveCustomOutlet(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-outlet-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['media-outlets'] });
      toast.success('Custom outlet approved and added to media outlets');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to approve custom outlet');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      customOutletReviewService.rejectCustomOutlet(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-outlet-reviews'] });
      toast.success('Custom outlet rejected');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reject custom outlet');
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ id, outletId }: { id: string; outletId: string }) =>
      customOutletReviewService.mergeToExistingOutlet(id, outletId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-outlet-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['media-outlets'] });
      toast.success('Custom outlet merged with existing outlet');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to merge custom outlet');
    },
  });

  const handleApprove = (review: CustomOutletReview) => {
    setSelectedReview(review);
    setActionType('approve');
  };

  const handleReject = (review: CustomOutletReview) => {
    setSelectedReview(review);
    setActionType('reject');
  };

  const handleMerge = (review: CustomOutletReview) => {
    setSelectedReview(review);
    setActionType('merge');
  };

  const handleCloseDialog = () => {
    setSelectedReview(null);
    setActionType(null);
  };

  const handleConfirmAction = async (data: { outletId?: string; reason?: string }) => {
    if (!selectedReview) return;

    if (actionType === 'approve') {
      approveMutation.mutate(selectedReview.id);
    } else if (actionType === 'reject' && data.reason) {
      rejectMutation.mutate({ id: selectedReview.id, reason: data.reason });
    } else if (actionType === 'merge' && data.outletId) {
      mergeMutation.mutate({ id: selectedReview.id, outletId: data.outletId });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      pending: 'secondary',
      approved: 'default',
      rejected: 'destructive',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Custom Outlet Review Queue</h3>
          <p className="text-sm text-muted-foreground">
            Review and approve custom media outlets submitted during audience mode
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading review queue...
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No pending reviews
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Outlet Name</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Suggested Match</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell className="font-medium">
                      {review.custom_outlet_name}
                    </TableCell>
                    <TableCell>{review.submission_count}</TableCell>
                    <TableCell>{getStatusBadge(review.review_status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        Match suggested if available
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {review.review_status === 'pending' && (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(review)}
                            className="gap-1"
                          >
                            <Check className="h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMerge(review)}
                            className="gap-1"
                          >
                            <LinkIcon className="h-4 w-4" />
                            Merge
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReject(review)}
                            className="gap-1"
                          >
                            <X className="h-4 w-4" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <ReviewActionDialog
        open={!!actionType}
        onOpenChange={handleCloseDialog}
        actionType={actionType}
        review={selectedReview}
        onConfirm={handleConfirmAction}
        isLoading={
          approveMutation.isPending ||
          rejectMutation.isPending ||
          mergeMutation.isPending
        }
      />
    </div>
  );
}
